// src/components/Button.tsx
import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'gradient';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  children,
  icon,
  className = '',
  href,
  target,
  rel,
  ...props
}) => {
  const baseStyles = 'inline-flex items-center justify-center rounded-xl font-bold transition-all duration-300 ease-in-out group focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-300';

  type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'gradient';

  const variantStyles: Record<ButtonVariant, string> = {
    primary: 'bg-white text-void-blue hover:bg-gray-100 shadow-[0_0_30px_rgba(255,255,255,0.1)] hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] font-bold',
    secondary: 'glass-panel text-white border border-white/20 hover:bg-white/10 hover:border-white/40 hover:shadow-[0_0_20px_rgba(139,92,246,0.3)]',
    ghost: 'text-white hover:bg-white/10',
    gradient: 'bg-gradient-ai text-white shadow-[0_0_30px_rgba(139,92,246,0.4)] hover:shadow-[0_0_40px_rgba(139,92,246,0.6)] font-bold',
  };

  const sizeStyles = {
    sm: 'px-5 py-2 text-sm',
    md: 'px-6 py-2.5 text-base',
    lg: 'px-8 py-3 text-lg',
  };

  const content = (
    <>
      <span>{children}</span>
      {icon && <span className="ml-3 transition-transform duration-300 group-hover:translate-x-1">{icon}</span>}
    </>
  );

  const styles = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`;

  if (href) {
    return <a href={href} target={target} rel={rel} className={styles}>{content}</a>;
  }

  return <button className={styles} {...props}>{content}</button>;
};

export default Button;
